import { z } from 'zod';
import { ModuleTypeEnum } from '../module';
import { createResponseSchema, createPaginatedResponseSchema } from '../pagination';
import { TaskSchema } from './base';

// Task group colors
export const TaskGroupColors = [
  '#316bb4', '#2a5c9a', '#24507f', '#1e4369',
  '#475569', '#6b7280', '#78716c', '#52525b',
  '#b91c1c', '#dc2626', '#ef4444', '#f87171',
  '#c2410c', '#ea580c', '#f97316', '#fb923c',
  '#a16207', '#ca8a04', '#eab308', '#facc15',
  '#4d7c0f', '#65a30d', '#84cc16', '#a3e635',
  '#15803d', '#16a34a', '#22c55e', '#4ade80',
  '#0f766e', '#0d9488', '#14b8a6', '#2dd4bf',
  '#155e75', '#0891b2', '#06b6d4', '#22d3ee',
  '#0369a1', '#0284c7', '#0ea5e9', '#38bdf8',
  '#4338ca', '#4f46e5', '#6366f1', '#818cf8',
  '#6d28d9', '#7c3aed', '#8b5cf6', '#a78bfa',
  '#a21caf', '#c026d3', '#d946ef', '#e879f9',
  '#be185d', '#db2777', '#ec4899', '#f472b6'
] as const;

// Default color for task groups
export const DEFAULT_TASK_GROUP_COLOR = TaskGroupColors[0];

// Task group status source enum - enhanced with template option
export enum TaskGroupStatusSourceEnum {
  INHERIT = 'inherit',      // Inherit from project
  TEMPLATE = 'template',    // Use status template
  CUSTOM = 'custom'         // Custom statuses
}

// Task group settings interface for different project types
export interface TaskGroupSettings {
  // Waterfall phase settings
  isWaterfallPhase?: boolean;
  phaseDependencies?: string[]; // IDs of prerequisite phases
  allowParallelTasks?: boolean;
  requiresApproval?: boolean;
  approvers?: string[]; // User IDs
  completionCriteria?: string;
  
  // Kanban column settings
  isKanbanColumn?: boolean;
  wipLimit?: number;
  
  // Scrum epic settings
  isEpic?: boolean;
  storyPointsTarget?: number;
  
  // Generic settings
  [key: string]: any;
}

// Task group schema
export const TaskGroupSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  moduleType: z.nativeEnum(ModuleTypeEnum).nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  parentId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  startDate: z.date().nullable().optional(),
  endDate: z.date().nullable().optional(),
  order: z.string().default("a1"),
  isCollapsed: z.boolean().default(false),
  settings: z.any().default({}),
  statusSource: z.nativeEnum(TaskGroupStatusSourceEnum).default(TaskGroupStatusSourceEnum.INHERIT),
  statusTemplateId: z.string().uuid().nullable().optional(),
  customStatuses: z.any().nullable().optional(),
  // Task type configuration: allowed types for the group + the default that
  // pre-fills new tasks created in it (soft default; not enforced).
  defaultTaskTypeDefId: z.string().uuid().nullable().optional(),
  allowedTaskTypeDefIds: z.array(z.string().uuid()).default([]),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type TaskGroup = z.infer<typeof TaskGroupSchema>;

// Create task group schema
export const CreateTaskGroupSchema = TaskGroupSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  settings: true,
}).extend({
  settings: z.any().optional(),
});

export type CreateTaskGroup = z.infer<typeof CreateTaskGroupSchema>;

// Update task group schema
export const UpdateTaskGroupSchema = TaskGroupSchema.omit({
  id: true,
  organizationId: true,
  moduleType: true,
  moduleId: true,
  createdAt: true,
  updatedAt: true,
}).partial();

export type UpdateTaskGroup = z.infer<typeof UpdateTaskGroupSchema>;

// Task with group schema
export const TaskWithGroupSchema = TaskSchema.extend({
  group: TaskGroupSchema.optional(),
});

export type TaskWithGroup = z.infer<typeof TaskWithGroupSchema>;

// Task group with tasks schema
export const TaskGroupWithTasksSchema = TaskGroupSchema.extend({
  tasks: z.array(TaskSchema).optional(),
});

export type TaskGroupWithTasks = z.infer<typeof TaskGroupWithTasksSchema>;

// Task group with counts schema
export const TaskGroupWithCountsSchema = TaskGroupSchema.extend({
  taskCounts: z.object({
    total: z.number(),
    completed: z.number(), // Includes both COMPLETED and CANCELED tasks
    notCompleted: z.number(), // Tasks that are NOT_STARTED, IN_PROGRESS, or ON_HOLD
  }),
});

export type TaskGroupWithCounts = z.infer<typeof TaskGroupWithCountsSchema>;

// Nested task group schema (for hierarchical groups)
export const TaskGroupNestedSchema: z.ZodType<any> = TaskGroupSchema.extend({
  tasks: z.array(TaskSchema).optional(),
  children: z.lazy(() => z.array(TaskGroupNestedSchema)).optional(),
  parent: TaskGroupSchema.nullable().optional(),
});

export type TaskGroupNested = z.infer<typeof TaskGroupNestedSchema>;

// Task group response schemas using factories
export const TaskGroupResponseSchema = createResponseSchema(TaskGroupSchema);
export const TaskGroupsResponseSchema = createPaginatedResponseSchema(TaskGroupSchema);
export const TaskGroupsWithCountsResponseSchema = createPaginatedResponseSchema(TaskGroupWithCountsSchema);

export type TaskGroupResponse = z.infer<typeof TaskGroupResponseSchema>;
export type TaskGroupsResponse = z.infer<typeof TaskGroupsResponseSchema>;
export type TaskGroupsWithCountsResponse = z.infer<typeof TaskGroupsWithCountsResponseSchema>;

// ============================================================================
// Move/Duplicate Task Group DTOs
// ============================================================================

// Status mapping for cross-project move/duplicate operations
export const GroupMoveStatusMappingSchema = z.object({
  sourceId: z.string(),
  sourceName: z.string(),
  sourceColor: z.string().optional(),
  targetId: z.string(),
  targetName: z.string(),
  targetColor: z.string().optional(),
  isExactMatch: z.boolean(),
});

export type GroupMoveStatusMapping = z.infer<typeof GroupMoveStatusMappingSchema>;

// Move Task Group to Project DTO
export const MoveTaskGroupSchema = z.object({
  targetProjectId: z.string().uuid(),
});

export type MoveTaskGroup = z.infer<typeof MoveTaskGroupSchema>;

// Duplicate Task Group to Project DTO
export const DuplicateTaskGroupToProjectSchema = z.object({
  targetProjectId: z.string().uuid(),
  targetGroupId: z.string().uuid().nullable().optional(), // null = create new group
  newGroupName: z.string().optional(), // Only if creating new group
  includeAssignees: z.boolean().default(false),
  includeSubtasks: z.boolean().default(true),
  statusMappings: z.array(GroupMoveStatusMappingSchema).optional(),
});

export type DuplicateTaskGroupToProject = z.infer<typeof DuplicateTaskGroupToProjectSchema>;

// Move/Duplicate Preview Response
export const MovePreviewSchema = z.object({
  sourceGroupId: z.string().uuid(),
  sourceGroupName: z.string(),
  targetProjectId: z.string().uuid(),
  targetProjectName: z.string(),
  taskCount: z.number(),
  subtaskCount: z.number(),
  statusMappings: z.array(GroupMoveStatusMappingSchema),
  unmappedStatuses: z.array(z.object({
    statusId: z.string(),
    statusName: z.string(),
    taskCount: z.number(),
  })).optional(),
  assigneesInTargetProject: z.number().optional(),
  assigneesNotInTargetProject: z.number().optional(),
  assigneeWarnings: z.array(z.object({
    userId: z.string(),
    userName: z.string(),
  })).optional(),
});

export type MovePreview = z.infer<typeof MovePreviewSchema>;

// Move/Duplicate Result Response
export const MoveResultSchema = z.object({
  group: TaskGroupSchema,
  movedTasks: z.number(),
  movedSubtasks: z.number(),
  statusesMapped: z.number(),
});

export type MoveResult = z.infer<typeof MoveResultSchema>;

export const DuplicateResultSchema = z.object({
  group: TaskGroupSchema,
  duplicatedTasks: z.number(),
  duplicatedSubtasks: z.number(),
  assigneesCopied: z.number().optional(),
});

export type DuplicateResult = z.infer<typeof DuplicateResultSchema>;
